import { AlertTriangle, CheckCircle2, CircleAlert, Info, ShieldAlert, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FindingStatus, Severity, SeverityCounts } from '../../types/data-integrity.js';

/** @graph-connects none */
export const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

/** @graph-connects none */
export const STATUS_LABEL_KEYS: Record<FindingStatus, string> = {
  open: 'dataIntegrity.statusOpen',
  acknowledged: 'dataIntegrity.statusAcknowledged',
  in_progress: 'dataIntegrity.statusInProgress',
  resolved: 'dataIntegrity.statusResolved',
  wont_fix: 'dataIntegrity.statusWontFix',
};

/** @graph-connects none */
export const STATUS_TRANSITIONS: Record<FindingStatus, FindingStatus[]> = {
  open: ['acknowledged', 'in_progress', 'resolved', 'wont_fix'],
  acknowledged: ['in_progress', 'resolved', 'wont_fix'],
  in_progress: ['resolved', 'wont_fix'],
  resolved: ['open'],
  wont_fix: ['open'],
};

/** @graph-connects none */
const SEVERITY_ICON = {
  critical: XCircle,
  high: ShieldAlert,
  medium: AlertTriangle,
  low: CircleAlert,
  info: Info,
} as const;

/** @graph-connects none */
const SEVERITY_LABEL_KEY: Record<Severity, string> = {
  critical: 'dataIntegrity.severityCritical',
  high: 'dataIntegrity.severityHigh',
  medium: 'dataIntegrity.severityMedium',
  low: 'dataIntegrity.severityLow',
  info: 'dataIntegrity.severityInfo',
};

/** @graph-connects none */
const SEVERITY_CLS: Record<Severity, string> = {
  critical: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  medium: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  info: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20',
};

/** @graph-connects none */
export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation();
  const Icon = SEVERITY_ICON[severity];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${SEVERITY_CLS[severity]}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {t(SEVERITY_LABEL_KEY[severity])}
    </span>
  );
}

/** @graph-connects none */
export function StatusBadge({ status }: { status: FindingStatus }) {
  const { t } = useTranslation();
  const config: Record<FindingStatus, { cls: string }> = {
    open: { cls: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
    acknowledged: {
      cls: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20',
    },
    in_progress: { cls: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
    resolved: { cls: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20' },
    wont_fix: { cls: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20' },
  };
  return (
    <span
      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${config[status].cls}`}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}

/** @graph-connects none */
export function SeveritySummaryBar({ counts }: { counts: SeverityCounts }) {
  const { t } = useTranslation();
  const items: { severity: Severity; count: number }[] = [
    { severity: 'critical', count: counts.critical },
    { severity: 'high', count: counts.high },
    { severity: 'medium', count: counts.medium },
    { severity: 'low', count: counts.low },
    { severity: 'info', count: counts.info },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      {items
        .filter((i) => i.count > 0)
        .map((item) => (
          <div key={item.severity} className="flex items-center gap-1.5">
            <SeverityBadge severity={item.severity} />
            <span className="text-sm font-mono font-bold text-[var(--color-text-primary)]">
              {item.count}
            </span>
          </div>
        ))}
      {items.every((i) => i.count === 0) && (
        <div className="flex items-center gap-2">
          <CheckCircle2
            className="h-5 w-5 text-[var(--color-success,#22c55e)]"
            aria-hidden="true"
          />
          <span className="text-sm text-[var(--color-success,#22c55e)] font-medium">
            {t('dataIntegrity.noInconsistency')}
          </span>
        </div>
      )}
    </div>
  );
}
